"use client";

import { useEffect, useMemo, useRef, useState, useSyncExternalStore, useTransition } from "react";
import Image from "next/image";
import { useTranslations } from "next-intl";
import { ArrowRight, Sparkles } from "lucide-react";
import { defaultLocale, formatPrice, isValidLocale, type Locale } from "@/config/locale";
import { usePricingResolvePresentations } from "@/lib/pricing/use-pricing-resolve-presentations";
import { normalizeSaleorMediaUrl } from "@/lib/media-url";
import { UPSELL_BESTSELLERS } from "@/config/brand";
import { resolveFreeShippingThreshold } from "@/checkout/views/saleor-checkout/free-shipping-core";
import { findPackUpgrade, type PackUpgradeOffer } from "@/lib/cart/pack-upgrade";
import { findPendingUpgrade, type UpgradeResult } from "@/lib/cart/pack-upgrade-operation";
import {
	parsePendingUpgrade,
	readPendingUpgrade,
	replacePendingUpgrade,
	subscribePendingUpgrade,
	type PendingPackUpgrade,
	readReviewedUpgrade,
	parseReviewedUpgrade,
	saveReviewedUpgrade,
} from "@/lib/cart/pack-upgrade-pending";
import {
	acknowledgesUpgradeSnapshot,
	reviewFingerprint,
	type UpgradeCartReviewResult,
} from "@/lib/cart/pack-upgrade-review";
import { addCartLine, upgradeCartLine, readUpgradeCartReview } from "./actions";
import { fetchUpsellProduct } from "./fetch-upsell-product";

interface CartUpsellProps {
	lines: Array<{
		id: string;
		variantId: string;
		variantName: string;
		productSlug: string;
		productName: string;
		thumbnailUrl?: string;
		totalPrice: number;
		quantity?: number;
		metadata?: { key: string; value: string }[];
	}>;
	subtotal: number;
	checkoutId: string;
	channel: string;
	locale: string;
	mutationsDisabled?: boolean;
	acquireMutation?: () => boolean;
	releaseMutation?: () => void;
	onCartChanged?: () => Promise<void> | void;
	onUpgradeComplete?: (replacementLineId: string) => void;
	onUpgradePendingChange?: (pending: boolean) => void;
	/** Display currency pinned by the surrounding page; null keeps the visitor's own. */
	referenceCurrencyOverride?: string | null;
}
interface UpgradeOffer extends PackUpgradeOffer {
	type: "upgrade";
	lineId: string;
	productName: string;
	thumbnailUrl: string;
	sourcePrice: number;
}
interface BestsellerOffer {
	type: "bestseller";
	productSlug: string;
	productName: string;
	thumbnailUrl: string;
	cheapestVariantId: string;
	cheapestPrice: number;
	currency: string;
	amountToFreeShipping: number;
}
type UpsellOffer = UpgradeOffer | BestsellerOffer;
export function CartUpsell({
	lines,
	subtotal,
	checkoutId,
	channel,
	locale,
	mutationsDisabled = false,
	acquireMutation,
	releaseMutation,
	onCartChanged,
	onUpgradeComplete,
	onUpgradePendingChange,
	referenceCurrencyOverride = null,
}: CartUpsellProps) {
	const t = useTranslations("cart");
	const loc = locale && isValidLocale(locale) ? (locale as Locale) : defaultLocale;
	const [isPending, startTransition] = useTransition();
	const inFlight = useRef(false);
	const [offer, setOffer] = useState<UpsellOffer | null>(null);
	const [result, setResult] = useState<UpgradeResult | null>(null);
	const [review, setReview] = useState<UpgradeCartReviewResult | null>(null);
	const stored = useSyncExternalStore(
		subscribePendingUpgrade,
		() => readPendingUpgrade(checkoutId),
		() => null,
	);
	const persisted = parsePendingUpgrade(stored, checkoutId);
	const snapshot = lines.map((l) => ({
		id: l.id,
		quantity: l.quantity ?? 1,
		variantId: l.variantId,
		metadata: l.metadata ?? [],
	}));
	const detected = findPendingUpgrade(checkoutId, snapshot);
	const rawPending = persisted ?? detected;
	const storedReview = useSyncExternalStore(
		subscribePendingUpgrade,
		() => readReviewedUpgrade(checkoutId),
		() => null,
	);
	const acknowledgement = parseReviewedUpgrade(storedReview, checkoutId);
	const acknowledged =
		rawPending?.resumeHint.phase === "adding" &&
		acknowledgesUpgradeSnapshot(acknowledgement, checkoutId, rawPending.operationId, snapshot);
	const pending = acknowledged ? null : rawPending;
	const unresolved = Boolean(pending);
	useEffect(() => {
		onUpgradePendingChange?.(unresolved);
	}, [unresolved, onUpgradePendingChange]);
	const threshold = resolveFreeShippingThreshold(channel);
	useEffect(() => {
		let cancelled = false;
		async function resolveOffer() {
			for (const line of lines) {
				try {
					const product = await fetchUpsellProduct(line.productSlug, channel, locale);
					if (cancelled) return;
					if (!product) continue;
					const variants = product.variants.flatMap((v) => (v.pack ? [v.pack] : []));
					const current = variants.find((v) => v.id === line.variantId);
					if (!current) continue;
					const upgrade = findPackUpgrade(current, line.quantity ?? 1, variants, "pack");
					if (upgrade) {
						setOffer({
							...upgrade,
							type: "upgrade",
							lineId: line.id,
							productName: line.productName,
							thumbnailUrl: normalizeSaleorMediaUrl(product.thumbnailUrl || line.thumbnailUrl),
							sourcePrice: current.price,
						});
						return;
					}
				} catch {
					/* Continue to next candidate. */
				}
			}
			const slugs = new Set(lines.map((l) => l.productSlug));
			for (const slug of UPSELL_BESTSELLERS) {
				if (slugs.has(slug)) continue;
				try {
					const product = await fetchUpsellProduct(slug, channel, locale);
					if (cancelled) return;
					if (!product) continue;
					const cheapest = product.variants
						.filter(
							(v) =>
								Number.isFinite(v.price) &&
								v.price >= 0 &&
								(v.quantityAvailable == null || v.quantityAvailable > 0),
						)
						.sort((a, b) => a.price - b.price)[0];
					if (cheapest) {
						setOffer({
							type: "bestseller",
							productSlug: slug,
							productName: product.name,
							thumbnailUrl: normalizeSaleorMediaUrl(product.thumbnailUrl),
							cheapestVariantId: cheapest.id,
							cheapestPrice: cheapest.price,
							currency: cheapest.currency,
							amountToFreeShipping: Math.max(0, threshold - subtotal),
						});
						return;
					}
				} catch {
					/* Next bestseller. */
				}
			}
			if (!cancelled) setOffer(null);
		}
		void resolveOffer();
		return () => {
			cancelled = true;
		};
	}, [lines, channel, locale, subtotal, threshold, result]);
	const moneyItems = useMemo(
		() =>
			!offer
				? []
				: offer.type === "upgrade"
					? [
							{ id: "target", amount: offer.targetPackPrice, currency: offer.currency },
							{ id: "unit", amount: offer.targetUnitPrice, currency: offer.currency },
							{ id: "delta", amount: offer.merchandiseDelta, currency: offer.currency },
							...(offer.savingsVsSingles !== null && offer.savingsVsSingles > 0
								? [{ id: "savings", amount: offer.savingsVsSingles, currency: offer.currency }]
								: []),
						]
					: [
							{ id: "cheapest", amount: offer.cheapestPrice, currency: offer.currency },
							{ id: "freeShip", amount: offer.amountToFreeShipping, currency: offer.currency },
						],
		[offer],
	);
	const money = usePricingResolvePresentations(locale, channel, moneyItems, referenceCurrencyOverride);
	const label = (id: string, amount: number, currency: string) =>
		money.get(id)?.primaryLabel ?? formatPrice(amount, currency, loc);
	const acquire = () => {
		if (inFlight.current || isPending || mutationsDisabled) return false;
		if (acquireMutation && !acquireMutation()) return false;
		inFlight.current = true;
		return true;
	};
	const finish = () => {
		inFlight.current = false;
		releaseMutation?.();
	};
	async function changePack(intent: "start" | "resume" | "cancel") {
		if (!acquire()) return;
		// Storage events may not have reached this render yet.
		if (readPendingUpgrade(checkoutId) !== stored) {
			window.dispatchEvent(new Event("cart-pack-upgrade"));
			finish();
			return;
		}
		let expected = stored;
		const commit = async (next: PendingPackUpgrade | null) => {
			if (!(await replacePendingUpgrade(checkoutId, expected, next))) return false;
			expected = next ? JSON.stringify(next) : null;
			return true;
		};
		const staleResponse = () => {
			const latest = parsePendingUpgrade(readPendingUpgrade(checkoutId), checkoutId);
			setResult(
				latest
					? { status: "unknown", operationId: latest.operationId, phase: latest.resumeHint.phase }
					: null,
			);
		};
		const selected = offer?.type === "upgrade" ? offer : null;
		let operation: PendingPackUpgrade | null = pending;
		if (intent === "start") {
			if (!selected || operation) {
				finish();
				return;
			}
			operation = {
				checkoutId,
				operationId: crypto.randomUUID(),
				resumeHint: { phase: "adding", sourceLineId: selected.lineId, sourceQuantity: selected.quantity },
			};
		}
		if (!operation) {
			finish();
			return;
		}
		let ready = operation;
		// A read-only check leaves the writer's marker untouched. Carry its confirmed
		// replacement identity into the next claimed write for lost-cancel recovery.
		if (
			result?.operationId === ready.operationId &&
			result.status === "partial" &&
			result.replacementLineId
		) {
			ready = { ...ready, resumeHint: { ...ready.resumeHint, replacementLineId: result.replacementLineId } };
		}
		const checkOnly =
			intent === "resume" &&
			(result?.status !== "partial" || ready.resumeHint.phase !== "adding" || !navigator.locks?.request);
		if (intent === "cancel" && ready.resumeHint.phase !== "adding") {
			finish();
			return;
		}
		let sentPhase =
			intent === "cancel"
				? ("cancelling" as const)
				: intent === "resume" && !checkOnly
					? ("removing_source" as const)
					: ready.resumeHint.phase;
		// Each write owns a distinct marker. Read-only checks retain their captured
		// marker, so a delayed reader cannot roll back a newer write phase.
		if (!checkOnly || expected === null) {
			ready = { ...ready, requestId: crypto.randomUUID() };
			if (!(await commit({ ...ready, resumeHint: { ...ready.resumeHint, phase: sentPhase } }))) {
				finish();
				return;
			}
		}
		startTransition(async () => {
			try {
				let response = await upgradeCartLine(
					intent === "start" && selected
						? {
								intent,
								checkoutId,
								operationId: ready.operationId,
								sourceLineId: selected.lineId,
								sourceVariantId: selected.sourceVariantId,
								sourceQuantity: selected.quantity,
								targetVariantId: selected.targetVariantId,
								channel,
								expectedSourcePrice: selected.sourcePrice,
								expectedTargetPrice: selected.targetPackPrice,
							}
						: {
								intent: intent === "start" ? "resume" : intent,
								checkoutId,
								operationId: ready.operationId,
								resumeHint: ready.resumeHint,
								checkOnly,
							},
				);
				if (readPendingUpgrade(checkoutId) !== expected) {
					staleResponse();
					return;
				}
				if (
					intent === "start" &&
					response.status === "partial" &&
					response.phase === "adding" &&
					response.replacementLineId &&
					!response.reason
				) {
					const replacementReady = {
						...ready.resumeHint,
						phase: "adding" as const,
						replacementLineId: response.replacementLineId,
					};
					sentPhase = "removing_source";
					if (
						await commit({
							...ready,
							requestId: crypto.randomUUID(),
							resumeHint: { ...replacementReady, phase: sentPhase },
						})
					) {
						response = await upgradeCartLine({
							intent: "resume",
							checkoutId,
							operationId: ready.operationId,
							resumeHint: replacementReady,
						});
					} else {
						response = {
							status: "unknown",
							operationId: ready.operationId,
							phase: "adding",
							replacementLineId: response.replacementLineId,
						};
					}
				}
				if (readPendingUpgrade(checkoutId) !== expected) {
					staleResponse();
					return;
				}
				if (["complete", "cancelled", "rejected"].includes(response.status)) {
					if (!(await commit(null))) {
						staleResponse();
						return;
					}
					setResult(response);
					onUpgradePendingChange?.(false);
					if (response.status === "rejected") setOffer(null);
				} else if (checkOnly) {
					// Observers cannot take ownership from a request whose response is still in flight.
					setResult(response);
				} else {
					if (
						!(await commit({
							...ready,
							requestId: crypto.randomUUID(),
							resumeHint: {
								...ready.resumeHint,
								phase: response.phase ?? sentPhase,
								...(response.replacementLineId ? { replacementLineId: response.replacementLineId } : {}),
							},
						}))
					) {
						staleResponse();
						return;
					}
					setResult(response);
				}
				await onCartChanged?.();
				if (response.status === "complete" && response.replacementLineId)
					onUpgradeComplete?.(response.replacementLineId);
			} catch {
				staleResponse();
			} finally {
				finish();
			}
		});
	}
	const canReview =
		result?.status === "conflict" &&
		pending?.resumeHint.phase === "adding" &&
		snapshot.some((line) =>
			line.metadata.some((m) => m.key === "cart_upgrade_operation" && m.value === pending.operationId),
		);
	function inspectCart() {
		if (!canReview || !pending || !acquire()) return;
		setReview(null);
		const operation = pending;
		startTransition(async () => {
			try {
				setReview(await readUpgradeCartReview(operation, locale));
				await onCartChanged?.();
			} catch {
				setReview({ status: "unavailable" });
			} finally {
				finish();
			}
		});
	}
	const reviewMatches = Boolean(
		canReview &&
		review?.status === "ready" &&
		pending &&
		review.operationId === pending.operationId &&
		review.checkoutId === checkoutId &&
		review.fingerprint === reviewFingerprint(snapshot),
	);
	async function acceptReviewedCart() {
		if (!reviewMatches || review?.status !== "ready" || inFlight.current || isPending || mutationsDisabled)
			return;
		const expected = readPendingUpgrade(checkoutId);
		const latest = parsePendingUpgrade(expected, checkoutId);
		if (latest?.operationId !== review.operationId || latest.resumeHint.phase !== "adding") return;
		if (await saveReviewedUpgrade(expected, review)) onUpgradePendingChange?.(false);
	}
	function addBestseller(selected: BestsellerOffer) {
		if (pending || !acquire()) return;
		startTransition(async () => {
			try {
				await addCartLine(checkoutId, selected.cheapestVariantId);
				await onCartChanged?.();
			} finally {
				finish();
			}
		});
	}
	const canContinue =
		result?.status === "partial" &&
		pending?.resumeHint.phase === "adding" &&
		Boolean(navigator.locks?.request);
	const status =
		isPending && pending
			? "packPending"
			: result?.status === "complete"
				? "packComplete"
				: result?.status === "cancelled"
					? "packCancelled"
					: result?.status === "rejected"
						? "packRejected"
						: result?.status === "conflict"
							? "packConflict"
							: pending
								? canContinue
									? "packPartial"
									: "packUnknown"
								: null;
	if (acknowledged || (!offer && !status)) return null;
	return (
		<div className="px-5 py-2.5">
			{status && (
				<p role="status" aria-live="polite" className="mb-2 text-sm text-muted-foreground">
					{t(status)}
				</p>
			)}
			{canReview && (
				<div className="mb-3 space-y-2">
					<button
						type="button"
						disabled={isPending || mutationsDisabled}
						onClick={inspectCart}
						className="min-h-11 rounded-md border border-border px-3 text-sm"
					>
						{t("packReview")}
					</button>
					{review?.status === "ready" && (
						<div className="rounded-md border border-border bg-background p-3">
							<p className="text-sm">{t("packReviewNotice")}</p>
							<ul className="my-3 space-y-2">
								{review.lines.map((line) => (
									<li key={line.id} className="text-sm">
										<p className="font-medium">{line.productName}</p>
										<p className="text-muted-foreground">{line.variantName}</p>
										<p>
											{t("packQuantity")}: {line.quantity}
										</p>
									</li>
								))}
							</ul>
							<button
								type="button"
								disabled={!reviewMatches || isPending || mutationsDisabled}
								onClick={acceptReviewedCart}
								className="min-h-11 rounded-md bg-primary px-3 text-sm text-primary-foreground"
							>
								{t("packReviewAccept")}
							</button>
						</div>
					)}
				</div>
			)}
			{pending ? (
				<div className="flex flex-wrap gap-2">
					<button
						type="button"
						disabled={isPending || mutationsDisabled}
						onClick={() => changePack("resume")}
						className="min-h-11 rounded-md bg-primary px-3 text-sm text-primary-foreground"
					>
						{t(canContinue ? "packResume" : "packCheck")}
					</button>
					{canContinue && (
						<button
							type="button"
							disabled={isPending || mutationsDisabled}
							onClick={() => changePack("cancel")}
							className="min-h-11 rounded-md border border-border px-3 text-sm"
						>
							{t("packCancel")}
						</button>
					)}
				</div>
			) : (
				offer && (
					<div className="flex flex-wrap items-center gap-3 rounded-lg border border-border bg-secondary p-3">
						{offer.thumbnailUrl && (
							<div className="relative h-12 w-10 shrink-0 overflow-hidden rounded bg-secondary">
								<Image
									src={offer.thumbnailUrl}
									alt={offer.productName}
									fill
									className="object-cover"
									sizes="40px"
								/>
							</div>
						)}
						<div className="min-w-0 flex-1">
							<p className="text-sm font-medium">{offer.productName}</p>
							{offer.type === "upgrade" ? (
								<div className="space-y-1 text-xs text-muted-foreground">
									<p>
										{t("packTarget", { quantity: offer.quantity, count: offer.targetPackSize })} ·{" "}
										{label("target", offer.targetPackPrice, offer.currency)}
									</p>
									<p>{t("packUnitPrice", { price: label("unit", offer.targetUnitPrice, offer.currency) })}</p>
									<p>{t("packDelta", { amount: label("delta", offer.merchandiseDelta, offer.currency) })}</p>
									{offer.savingsVsSingles !== null && offer.savingsVsSingles > 0 && (
										<p>
											{t("packSavings", { amount: label("savings", offer.savingsVsSingles, offer.currency) })}
										</p>
									)}
									<p>{t("packNote")}</p>
								</div>
							) : (
								<div className="text-xs text-muted-foreground">
									<p>
										<Sparkles className="mr-1 inline-block h-3 w-3" />
										{t("bestseller")} ·{" "}
										{t("startingFrom", { price: label("cheapest", offer.cheapestPrice, offer.currency) })}
									</p>
									{offer.amountToFreeShipping > 0 && (
										<p>
											{t("amountToFreeShipping", {
												amount: label("freeShip", offer.amountToFreeShipping, offer.currency),
											})}
										</p>
									)}
								</div>
							)}
						</div>
						<button
							type="button"
							disabled={isPending || mutationsDisabled}
							onClick={() => (offer.type === "upgrade" ? changePack("start") : addBestseller(offer))}
							className="inline-flex min-h-11 shrink-0 items-center gap-1 rounded-md bg-foreground px-3 text-xs font-medium text-background disabled:opacity-50"
						>
							{offer.type === "upgrade" ? t("upgrade") : t("addToCart")}
							<ArrowRight className="h-3 w-3" />
						</button>
					</div>
				)
			)}
		</div>
	);
}
